/**
 * Tenant dashboard — `GET reports/dashboard/`, same query key
 * (`['reports-dashboard']`) `ReportsDashboardPage.tsx` uses so TanStack Query
 * dedupes if a user visits both. This route only mounts for TENANT
 * (router.tsx), so the tenant response shape is the only one reachable
 * here — `isTenantDashboard` still guards it to satisfy the `Dashboard`
 * union type, not because the manager branch is really reachable.
 *
 * Deliberately lighter than ReportsDashboardPage's full `<dl>`-per-metric
 * layout: a handful of stat tiles, each linking into the list page that owns
 * the number, using the exact paths TenantShell's NAV_ITEMS already define.
 * `active_lease`/`next_payment` are nullable — both render a quiet fallback
 * tile rather than a broken link when there is nothing to show. Sectioned,
 * icon-badged 3-tile view, matching Flutter's own `tenant_dashboard.dart`
 * (`_LeaseSection`/`_RentDueSection`/`_MaintenanceSection`) — no extra
 * panels/queries beyond what `reports/dashboard/` already returns, since
 * Flutter's tenant dashboard is itself just those 3 sections.
 */
import { useQuery } from '@tanstack/react-query';
import { Column, Grid } from '@carbon/react';
import { Home, Money, ToolBox } from '@carbon/icons-react';
import { parseApiError } from '../../lib/apiError';
import { getDashboard } from '../../api/endpoints/reports';
import { isTenantDashboard } from '../../types/report';
import { StatTile } from '../../components/StatTile';

export function TenantHome(): React.JSX.Element {
  const query = useQuery({
    queryKey: ['reports-dashboard'],
    queryFn: () => getDashboard(),
  });

  if (query.isPending) return <p>Loading…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="form__error">
        {parseApiError(query.error).message}
      </p>
    );
  }

  const dashboard = query.data;
  if (!isTenantDashboard(dashboard)) return <></>;

  const openMaintenance = dashboard.maintenance_requests.new + dashboard.maintenance_requests.in_progress;

  return (
    <section className="tenant-home">
      <h2>Tenant Dashboard</h2>
      <div className="dashboard-stats">
        <Grid withRowGap>
          <Column sm={4} md={4} lg={4}>
            {dashboard.active_lease === null ? (
              <StatTile icon={Home} value="–" label="No active lease" />
            ) : (
              <StatTile
                icon={Home}
                value={dashboard.active_lease.rent_amount}
                label="Monthly rent"
                detail={`${dashboard.active_lease.property} · ${dashboard.active_lease.unit}`}
                secondaryDetail={`Lease ends ${dashboard.active_lease.end_date}`}
              />
            )}
          </Column>
          <Column sm={4} md={4} lg={4}>
            {dashboard.next_payment === null ? (
              <StatTile icon={Money} to="/tenant/payments" value="–" label="Nothing due" />
            ) : (
              <StatTile
                icon={Money}
                to="/tenant/payments"
                value={dashboard.next_payment.amount}
                label="Next payment"
                detail={`Due ${dashboard.next_payment.due_date}`}
                secondaryDetail={
                  dashboard.next_payment.status !== 'OPEN' ? dashboard.next_payment.status : undefined
                }
              />
            )}
          </Column>
          <Column sm={4} md={4} lg={4}>
            <StatTile
              icon={ToolBox}
              to="/tenant/maintenance"
              value={openMaintenance}
              label="Open maintenance requests"
              detail={`${dashboard.maintenance_requests.total} total`}
            />
          </Column>
        </Grid>
      </div>
    </section>
  );
}

export default TenantHome;
